import {auth, db} from './firebase';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    User as FirebaseUser
} from 'firebase/auth';
import {
    doc,
    setDoc,
    deleteDoc,
    collection,
    getDocs
} from 'firebase/firestore';
import {User, UserRole} from '../types';

const USERS_COLLECTION = 'user_roles';
type Role = "user" | "administrator" | "superadministrator";

export const authService = {
    isConfigured: !!auth && !!db,

    login: async (email: string, password: string): Promise<void> => {
        if (!auth) throw new Error("Firebase není nakonfigurován.");
        await signInWithEmailAndPassword(auth, email.trim(), password);
    },

    register: async (email: string, password: string): Promise<void> => {
        if (!auth) throw new Error("Firebase není nakonfigurován.");
        await createUserWithEmailAndPassword(auth, email.trim(), password);
    },

    logout: async (): Promise<void> => {
        if (!auth) return;
        await signOut(auth);
    },

    onAuthStateChange: (callback: (user: User | null) => void) => {
        if (!auth || !db) {
            callback(null);
            return () => {
            };
        }

        return onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
            if (firebaseUser && firebaseUser.email) {
                const email = firebaseUser.email.toLowerCase();

                const tokenResult = await firebaseUser.getIdTokenResult();
                const roleClaim = tokenResult.claims.role as Role | undefined;
                const user: User = {
                    uid: firebaseUser.uid,
                    email: firebaseUser.email,
                    role: roleClaim ?? "user",
                };
                callback(user);
                return;
            } else {
                callback(null);
            }
        });
    },

    getUsers: async (): Promise<User[]> => {
        if (!db) return [];
        const snapshot = await getDocs(collection(db, USERS_COLLECTION));
        return snapshot.docs.map(doc => ({
            uid: doc.id,
            email: doc.id,
            role: (doc.data().role as UserRole) || 'user'
        }));
    },

    addUserRole: async (email: string, role: UserRole): Promise<void> => {
        if (!db) throw new Error("DB nedostupná.");
        await setDoc(doc(db, USERS_COLLECTION, email.trim().toLowerCase()), {role});
    },

    removeUserRole: async (email: string): Promise<void> => {
        if (!db) throw new Error("DB nedostupná.");
        await deleteDoc(doc(db, USERS_COLLECTION, email.trim().toLowerCase()));
    }
};
